import React, { useEffect, useMemo } from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { useSaveActionsContext } from "context/save";

import useApartmentRatesApi from "hooks/api/private/useApartmentRatesApi";

import { ApartmentRate } from "types/api/apartmentRates";

import { getExtraErrorsDescription } from "utils/validators";

export type ApartmentRatesFormType = {
    terrazza: string;
    retica: string;
};

export type ApartmentRatesFormHook = {
    formHook: FormHookType<ApartmentRatesFormType>;
};

function useApartmentRatesForm(): ApartmentRatesFormHook {
    const { getAllApartmentRates, updateApartmentRate } = useApartmentRatesApi();

    const schema = useMemo<ObjectPropertyType>(
        () => ({
            type: "object",
            properties: {
                terrazza: {
                    type: "simple",
                    validators: [],
                },
                retica: {
                    type: "simple",
                    validators: [],
                },
            },
        }),
        [],
    );

    const initialValues: ApartmentRatesFormType = React.useMemo(
        () => ({
            terrazza: "",
            retica: "",
        }),
        [],
    );

    const formHook = useForm<ApartmentRatesFormType>(schema, initialValues, getExtraErrorsDescription);

    const { setForm, isValid, form, showAllError } = formHook;

    // Load current rates on mount
    React.useEffect(() => {
        const [apiCall, abortController] = getAllApartmentRates();

        apiCall.then((rates: ApartmentRate[]) => {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                rates.forEach((rate) => {
                    newForm[rate.apartment] = rate.min_amount;
                });

                return newForm;
            });
        });

        return () => {
            abortController.abort();
        };
    }, [getAllApartmentRates, setForm]);

    const { addSaveAction } = useSaveActionsContext();

    useEffect(() => {
        addSaveAction("useApartmentRatesForm", () => {
            if (isValid) {
                const promises = [
                    updateApartmentRate("terrazza", { min_amount: parseFloat(form.terrazza) || 0 })[0],
                    updateApartmentRate("retica", { min_amount: parseFloat(form.retica) || 0 })[0],
                ];

                return Promise.all(promises);
            } else {
                showAllError();

                return Promise.reject(new Error("INVALID_FORM"));
            }
        });
    }, [addSaveAction, showAllError, updateApartmentRate, isValid, form]);

    return React.useMemo(
        (): ApartmentRatesFormHook => ({
            formHook,
        }),
        [formHook],
    );
}

export default useApartmentRatesForm;
